import MenuIcon from '@mui/icons-material/Menu';
import IconButton from '@mui/material/IconButton';
import { useQuery } from '@tanstack/react-query';
import { useMatchRoute } from '@tanstack/react-router';
import clsx from 'clsx';
import { useAtom } from 'jotai';
import { motion } from 'motion/react';
import { useRef, useState } from 'react';

import { ResizablePanel } from '#components/resizable-panel/resizable-panel.js';
import { useTRPC } from '#src/trpc.js';
import { sidebarWantsToFloatAtom, useSidebarIsFloating, useSidebarMayFloat } from '#state/atoms/sidebar-layout.js';
import { SidebarContent } from './sidebar-content.js';

/**
 * The app's navigation sidebar. Has three states: hidden (only a floating menu button at the
 * top-left of the screen), a floating popover shown while hovering the menu button, and pinned
 * open as a full-height column in the page layout (toggled by clicking the menu button; persisted
 * across sessions). In the hidden and popover states it is absolutely positioned, taking up no
 * space in the page layout.
 */
export function Sidebar() {
	const trpc = useTRPC();
	const matchRoute = useMatchRoute();
	const isInWizardApp = matchRoute({ to: '/wizard', fuzzy: true }) !== false;

	const [, setSidebarWantsToFloat] = useAtom(sidebarWantsToFloatAtom);
	const sidebarMayFloat = useSidebarMayFloat();
	const sidebarIsFloating = useSidebarIsFloating();
	const [popoverOpen, setPopoverOpen] = useState(false);
	const closeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

	const cancelScheduledClose = () => {
		if (closeTimeoutRef.current !== null) {
			clearTimeout(closeTimeoutRef.current);
			closeTimeoutRef.current = null;
		}
	};

	// Delay closing so moving the cursor between the trigger and the content (which fires a
	// `mouseleave` immediately followed by a `mouseenter`) doesn't dismiss the popover.
	const scheduleClose = () => {
		cancelScheduledClose();
		closeTimeoutRef.current = setTimeout(() => {
			setPopoverOpen(false);
			closeTimeoutRef.current = null;
		}, 160);
	};

	const openPopover = () => {
		cancelScheduledClose();
		setPopoverOpen(true);
	};

	const mode =
		sidebarIsFloating ?
			popoverOpen ? 'floating-visible'
			:	'floating-hidden'
		:	'fixed';

	const { data: currentUser } = useQuery(
		trpc.user.getCurrent.queryOptions(undefined, {
			throwOnError: false,
		}),
	);
	const isStaff = currentUser?.auth.isStaff;

	// Sidebar is hidden in wizard app unless you are staff
	if (isInWizardApp && !isStaff) {
		return null;
	}

	return (
		<>
			<div
				className={clsx(
					'pointer-events-none fixed top-0 left-1 z-15 grid size-8 place-items-center',
					!sidebarMayFloat && 'hidden',
				)}
			>
				<IconButton
					aria-label='Open sidebar'
					className='pointer-events-auto'
					onMouseEnter={openPopover}
					onMouseLeave={scheduleClose}
					onClick={() => setSidebarWantsToFloat((prev) => !prev)}
				>
					<MenuIcon />
				</IconButton>
			</div>

			<motion.div
				variants={{
					'floating-hidden': { opacity: 0 },
					'floating-visible': { opacity: 1 },
					fixed: { opacity: 1 },
				}}
				animate={mode}
				transition={{
					duration: 0.08,
				}}
				className={clsx(
					'flex flex-col overflow-hidden bg-gray-4',
					mode === 'floating-hidden' && 'pointer-events-none',
					mode !== 'fixed' && 'fixed top-8 left-1 z-10 rounded-md border border-gray-a3 shadow-xs',
					mode === 'fixed' && 'border-r border-gray-a3',
				)}
				onMouseEnter={openPopover}
				onMouseLeave={scheduleClose}
			>
				<ResizablePanel
					position='left'
					initialSize='16rem'
					minSize={180}
					disableContainSize
					className={clsx('flex min-h-0 grow! flex-col', mode === 'fixed' && 'ml-1.5')}
				>
					{mode === 'fixed' && (
						<div
							className={clsx(
								'flex h-8 items-center px-1 select-none',
								sidebarMayFloat ? 'justify-end' : 'justify-start',
							)}
						>
							<img src='/images/Cadstrom_DARK_PURPLE_LOGOMARK.png' alt='' className='h-4 shrink-0 object-contain' />
							<span className='px-2 text-sm font-semibold tracking-widest text-[oklch(from_--theme(--color-gray-11)_l_0.07_300)] uppercase'>
								Cadstrom
							</span>
						</div>
					)}

					<div
						className={clsx(
							'flex min-h-0 min-w-0 grow flex-col bg-gray-2',
							mode !== 'fixed' && 'max-h-[80svh]',
							mode === 'fixed' && 'm-px -ml-1.25 basis-0 rounded-t-md py-0 pl-1',
						)}
					>
						<SidebarContent />
					</div>
				</ResizablePanel>
			</motion.div>
		</>
	);
}
